import React, { useState, useContext, useEffect } from 'react'
import styled from 'styled-components'
import { Button, Typography } from 'antd'
import { FormattedMessage } from 'react-intl'

import { FlexRow } from '../Components/FlexboxReusables/FlexItems'
import { ThemeContext } from '../Theme/ThemeContext'
import { UserContext } from '../Components/User/UserContext'
import PersonalInfoSegment from '../Components/User/PersonalInfoSegment'
import AddressSegment from '../Components/User/AddressSegment'
import ProfileSummarySegment from '../Components/User/ProfileSummarySegment'
import { AuthContext } from '../Components/Auth/AuthContext'
import { SaveOutlined } from '@ant-design/icons'
import CompanySegment from '../Components/User/CompanySegment'

const notAvailable = '/'

const UserSettings: React.FC = () => {
  const { isDarkMode } = useContext(ThemeContext)
  const { authUser } = useContext(AuthContext)
  const { userDetails, changeUserDetails } = useContext(UserContext)

  const [ firstName, setFirstName ] = useState<string>(userDetails.firstName)
  const [ lastName, setLastName ] = useState<string>(userDetails.lastName)
  const [ email, setEmail ] = useState<string>(userDetails.email)
  const [ phone, setPhone ] = useState<string>(userDetails.phone || notAvailable)
  const [ position, setPosition ] = useState<string>(userDetails.position || notAvailable)

  const [ country, setCountry ] = useState<string>(userDetails.country || notAvailable)
  const [ state, setState ] = useState<string>(userDetails.state || notAvailable)
  const [ street, setStreet ] = useState<string>(userDetails.street || notAvailable)
  const [ zipCode, setZipCode ] = useState<string>(userDetails.zipCode || notAvailable)
  const [ city, setCity ] = useState<string>(userDetails.city || notAvailable)

  const [ companyName, setCompanyName ] = useState<string>(userDetails.company?.name || notAvailable)
  const [ companyStreet, setCompanyStreet ] = useState<string>(userDetails.company?.street || notAvailable)
  const [ companyZipCode, setCompanyZipCode ] = useState<string>(userDetails.company?.zipCode || notAvailable)
  const [ companyCity, setCompanyCity ] = useState<string>(userDetails.company?.city || notAvailable)
  const [ companyCountry, setCompanyCountry ] = useState<string>(userDetails.company?.country || notAvailable)

  const [ editProfilePicture, setEditProfilePicture ] = useState<boolean>(false)
  const [ editPersonalInfo, setEditPersonalInfo ] = useState<boolean>(false)
  const [ editAddress, setEditAddress ] = useState<boolean>(false)
  const [ editBuyersDetails, setEditBuyersDetails ] = useState<boolean>(false)

  useEffect(() => {
    setFirstName(userDetails.firstName)
    setLastName(userDetails.lastName)
    setEmail(userDetails.email)
    setPhone(userDetails.phone || notAvailable)
    setPosition(userDetails.position || notAvailable)

    setCountry(userDetails.country || notAvailable)
    setState(userDetails.state || notAvailable)
    setStreet(userDetails.street || notAvailable)
    setZipCode(userDetails.zipCode || notAvailable)
    setCity(userDetails.city || notAvailable)

    setCompanyName(userDetails.company?.name || notAvailable)
    setCompanyStreet(userDetails.company?.street || notAvailable)
    setCompanyZipCode(userDetails.company?.zipCode || notAvailable)
    setCompanyCity(userDetails.company?.city || notAvailable)
    setCompanyCountry(userDetails.company?.country || notAvailable)
  }, [ userDetails ])

  const onChangeUserDetails = () => {
    changeUserDetails(
      {
        id: authUser.id,
        firstName,
        lastName,
        email,
        phone,
        position,
        country,
        state,
        street,
        zipCode,
        city,
        company: userDetails.company ? {
          id: userDetails.company.id,
          name: companyName,
          street: companyStreet,
          zipCode: companyZipCode,
          city: companyCity,
          country: companyCountry
        } : undefined
      }
    )
  }

  return (
    <>
      <FlexRow width='100%' justifyContent='center' height='5em'>
        <Typography.Title level={2}>
          <FormattedMessage id='userSettings.title' />
        </Typography.Title>
      </FlexRow>
      <Container isDarkMode={isDarkMode}>
        <FlexRow height='2em' margin='0 0 2em 0' width='95%' justifyContent='flex-end'>
          <Button type='primary' onClick={onChangeUserDetails}>
            <SaveOutlined style={{marginRight: '1em'}}/>
            <FormattedMessage id='generic.save' />
          </Button>
        </FlexRow>
        <SegmentContainer>
          <ProfileSummarySegment
            id={authUser.id}
            firstName={firstName}
            lastName={lastName}
            companyName={companyName}
            position={position}
            city={city}
            state={state}
            setEditProfilePicture={setEditProfilePicture}
            editProfilePicture={editProfilePicture}
          />
        </SegmentContainer>

        <SegmentContainer>
          <PersonalInfoSegment
            firstName={firstName}
            lastName={lastName}
            email={email}
            phone={phone}
            position={position}
            setFirstName={setFirstName}
            setLastName={setLastName}
            setEmail={setEmail}
            setPhone={setPhone}
            setPosition={setPosition}
            setEditPersonalInfo={setEditPersonalInfo}
            editPersonalInfo={editPersonalInfo}
          />
        </SegmentContainer>

        <SegmentContainer>
          <CompanySegment
            id={userDetails.company?.id || 0}
            name={companyName}
            setCompanyName={setCompanyName}
            street={companyStreet}
            setCompanyStreet={setCompanyStreet}
            zipCode={companyZipCode}
            setCompanyZipCode={setCompanyZipCode}
            city={companyCity}
            setCompanyCity={setCompanyCity}
            country={companyCountry}
            setCompanyCountry={setCompanyCountry}
            setEditBuyersDetails={setEditBuyersDetails}
            editBuyersDetails={editBuyersDetails}
          />
        </SegmentContainer>

        <SegmentContainer>
          <AddressSegment
            country={country}
            state={state}
            street={street}
            zipCode={zipCode}
            city={city}
            setCountry={setCountry}
            setState={setState}
            setStreet={setStreet}
            setZipCode={setZipCode}
            setCity={setCity}
            setEditAddress={setEditAddress}
            editAddress={editAddress}
          />
        </SegmentContainer>

      </Container>
    </>
  )
}

export default UserSettings

const Container = styled.div<{isDarkMode: boolean}>`
  width: 95%;
  height: 100%;
  margin: 0 auto;
  padding-top: 2em;
  padding-bottom: 2em;
  border-radius: 2em;
  background-color: ${({ isDarkMode }) => isDarkMode ? '#1e1e1e' : 'white'};
`

const SegmentContainer = styled.div`
  width: 90%;
  margin: 0 auto 4em auto;
  padding: 1.5em;
  border-radius: 2em;
  border: 0.1em dashed darkgrey;
`